import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Building, 
  Package, 
  User, 
  Calendar, 
  CheckCircle, 
  Clock, 
  BarChart3,
  RefreshCw,
  Download
} from 'lucide-react';
import { Transaction, SapStatus } from '@/types';
import { apiService } from '@/services/api';
import toast from 'react-hot-toast';

interface SapStats {
  total: number;
  notTransferred: number;
  transferred: number;
  byPlantNumber: Record<string, number>;
  recentTransactions: Transaction[];
}

const SapInfoPage: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [stats, setStats] = useState<SapStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [transactionsResponse, statsResponse] = await Promise.all([
        apiService.get<Transaction[]>('/sap/transactions'),
        apiService.get<SapStats>('/sap/stats')
      ]);
      
      setTransactions(transactionsResponse || []);
      setStats(statsResponse || null);
    } catch (error) {
      console.error('Error fetching SAP data:', error);
      toast.error('Fehler beim Laden der SAP-Daten');
    } finally {
      setLoading(false);
    }
  };

  const updateSapStatus = async (transactionId: string, status: SapStatus) => {
    try {
      setUpdating(transactionId);
      await apiService.put(`/sap/transactions/${transactionId}/status`, { status });
      
      toast.success(`SAP-Status erfolgreich auf "${status === 'TRANSFERRED' ? 'Übertragen' : 'Noch nicht übertragen'}" gesetzt`);
      await fetchData(); // Daten neu laden
    } catch (error) {
      console.error('Error updating SAP status:', error);
      toast.error('Fehler beim Aktualisieren des SAP-Status');
    } finally {
      setUpdating(null);
    }
  };

  const getSapStatusBadge = (status: SapStatus) => {
    if (status === 'TRANSFERRED') {
      return (
        <Badge variant="default" className="bg-green-100 text-green-800 border-green-200">
          <CheckCircle className="h-3 w-3 mr-1" />
          Übertragen
        </Badge>
      );
    }
    return (
      <Badge variant="secondary" className="bg-orange-100 text-orange-800 border-orange-200">
        <Clock className="h-3 w-3 mr-1" />
        Noch nicht übertragen
      </Badge>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const exportToCSV = () => {
    const csvData = transactions.map(t => ({
      'Datum': formatDate(t.createdAt),
      'Item': t.item.name,
      'Material-Nr': t.item.sku || '',
      'Menge': t.quantity,
      'Werknummer': t.sapPlantNumber || '',
      'Benutzer': t.user?.name || '',
      'Status': t.sapStatus === 'TRANSFERRED' ? 'Übertragen' : 'Noch nicht übertragen',
      'Übertragen am': t.sapTransferredAt ? formatDate(t.sapTransferredAt) : '',
      'Übertragen von': t.sapTransferrer?.name || '',
      'Kommentar': t.notes || ''
    }));

    const csvContent = [
      Object.keys(csvData[0]).join(';'),
      ...csvData.map(row => Object.values(row).join(';'))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `sap-transaktionen-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-2" />
          <p>Lade SAP-Daten...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">SAP Info</h1>
          <p className="text-muted-foreground">
            Übersicht über SAP-geführte Items und deren Übertragungsstatus
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchData} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Aktualisieren
          </Button>
          <Button onClick={exportToCSV} variant="outline">
            <Download className="h-4 w-4 mr-2" />
            CSV Export
          </Button>
        </div>
      </div>

      {/* Statistiken */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <BarChart3 className="h-8 w-8 text-blue-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-muted-foreground">Gesamt</p>
                  <p className="text-2xl font-bold">{stats.total}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <Clock className="h-8 w-8 text-orange-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-muted-foreground">Nicht übertragen</p>
                  <p className="text-2xl font-bold">{stats.notTransferred}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <CheckCircle className="h-8 w-8 text-green-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-muted-foreground">Übertragen</p>
                  <p className="text-2xl font-bold">{stats.transferred}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <Building className="h-8 w-8 text-purple-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-muted-foreground">Werke</p>
                  <p className="text-2xl font-bold">{Object.keys(stats.byPlantNumber).length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Werknummern-Übersicht */}
      {stats && Object.keys(stats.byPlantNumber).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building className="h-5 w-5" />
              Entnahmen nach Werknummer
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {Object.entries(stats.byPlantNumber).map(([plantNumber, count]) => (
                <div key={plantNumber} className="text-center p-3 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">Werk {plantNumber}</p>
                  <p className="text-xl font-bold">{count}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Transaktionsliste */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            SAP-Transaktionen ({transactions.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {transactions.length === 0 ? (
            <div className="text-center py-8">
              <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">Keine SAP-Transaktionen gefunden</p>
            </div>
          ) : (
            <div className="space-y-4">
              {transactions.map((transaction) => (
                <div 
                  key={transaction.id} 
                  className="border rounded-lg p-4 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-4 mb-2">
                        <h3 className="font-semibold">{transaction.item.name}</h3>
                        {getSapStatusBadge(transaction.sapStatus)}
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center gap-2">
                          <Package className="h-4 w-4" />
                          <span>Mat.-Nr: {transaction.item.sku || '-'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Building className="h-4 w-4" />
                          <span>Werk: {transaction.sapPlantNumber || '-'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4" />
                          <span>{transaction.user?.name || 'Unbekannt'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4" />
                          <span>{formatDate(transaction.createdAt)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span>Menge: {transaction.quantity}</span>
                        </div>
                        {transaction.sapTransferredAt && (
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4" />
                            <span>Übertragen: {formatDate(transaction.sapTransferredAt)}</span>
                          </div>
                        )}
                      </div>
                      
                      {transaction.notes && (
                        <div className="mt-2 text-sm">
                          <strong>Kommentar:</strong> {transaction.notes}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex gap-2">
                      {transaction.sapStatus === 'NOT_TRANSFERRED' ? (
                        <Button
                          size="sm"
                          onClick={() => updateSapStatus(transaction.id, SapStatus.TRANSFERRED)}
                          disabled={updating === transaction.id}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          {updating === transaction.id ? (
                            <RefreshCw className="h-4 w-4 animate-spin" />
                          ) : (
                            <CheckCircle className="h-4 w-4" />
                          )}
                          Als übertragen markieren
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateSapStatus(transaction.id, SapStatus.NOT_TRANSFERRED)}
                          disabled={updating === transaction.id}
                        >
                          {updating === transaction.id ? (
                            <RefreshCw className="h-4 w-4 animate-spin" />
                          ) : (
                            <Clock className="h-4 w-4" />
                          )}
                          Zurücksetzen
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SapInfoPage;
